import { useState } from "react";
import { api } from "../api";
import { rupee } from "../format";
import type { Trade } from "../types";
import { EXCHANGES, PRODUCTS } from "../types";
import { SymbolSearch } from "./SymbolSearch";

interface Row {
  key: string;
  kind: "buy" | "sell";
  date: string;
  time: string;
  quantity: string;
  price: string;
  exchange: string;
  product: string;
}

let counter = 0;
const newKey = () => `row-${counter++}`;

const toRows = (trade: Trade): Row[] =>
  trade.legs.map((leg) => ({
    key: newKey(),
    kind: leg.kind,
    date: leg.leg_date,
    time: (leg.leg_time ?? "").slice(0, 5),
    quantity: String(leg.quantity),
    price: String(leg.price),
    exchange: leg.exchange ?? "NSE",
    product: leg.product ?? "cash",
  }));

/**
 * Edit a whole trade as one document: symbol, every fill, and the stop.
 *
 * Saving replaces the fills wholesale rather than diffing them, so removing a
 * row actually removes it. Nothing is written until Save, so an abandoned edit
 * leaves the trade exactly as it was.
 */
export function EditTrade({
  trade,
  onSaved,
  onClose,
}: {
  trade: Trade;
  onSaved: (t: Trade) => void;
  onClose: () => void;
}) {
  const [symbol, setSymbol] = useState(trade.symbol);
  const [rows, setRows] = useState<Row[]>(toRows(trade));
  const [sl, setSl] = useState(
    trade.journal?.sl_price === null || trade.journal?.sl_price === undefined
      ? ""
      : String(trade.journal.sl_price),
  );
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const update = (key: string, patch: Partial<Row>) =>
    setRows((list) => list.map((r) => (r.key === key ? { ...r, ...patch } : r)));

  const addRow = (kind: "buy" | "sell") =>
    setRows((list) => [
      ...list,
      {
        key: newKey(),
        kind,
        date: new Date().toISOString().slice(0, 10),
        time: "",
        quantity: "",
        price: "",
        // Inherit from the position rather than defaulting blindly: a scale-in
        // almost always uses the same venue and product as the original entry.
        exchange: list[0]?.exchange ?? "NSE",
        product: list[0]?.product ?? "cash",
      },
    ]);

  const buys = rows.filter((r) => r.kind === "buy");
  const sells = rows.filter((r) => r.kind === "sell");

  const totalQty = (list: Row[]) => list.reduce((a, r) => a + (Number(r.quantity) || 0), 0);
  const totalValue = (list: Row[]) =>
    list.reduce((a, r) => a + (Number(r.quantity) || 0) * (Number(r.price) || 0), 0);

  const buyQty = totalQty(buys);
  const sellQty = totalQty(sells);
  const avgEntry = buyQty ? totalValue(buys) / buyQty : 0;
  const oversold = sellQty > buyQty;

  const incomplete = rows.some((r) => !r.date || !r.quantity || !r.price);

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const saved = await api.editTrade(trade.id, {
        symbol: symbol.trim(),
        sl_price: sl.trim() === "" ? null : Number(sl),
        legs: rows.map((r) => ({
          kind: r.kind,
          leg_date: r.date,
          leg_time: r.time ? `${r.time}:00` : null,
          quantity: Number(r.quantity),
          price: Number(r.price),
          exchange: r.exchange,
          product: r.product,
        })),
      });
      onSaved(saved);
      onClose();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <strong>Edit Trade</strong>
          <button className="ghost right" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="modal-body">
          {error && <div className="banner error">{error}</div>}

          <label>Stock Name</label>
          <SymbolSearch value={symbol} onChange={setSymbol} />

          <FillGroup
            title="Buy Entries"
            rows={buys}
            onUpdate={update}
            onRemove={(key) => setRows((l) => l.filter((r) => r.key !== key))}
            onAdd={() => addRow("buy")}
            addLabel="Add buy entry"
            tone="buy"
            canRemove={buys.length > 1}
          />

          <div className="modal-section">
            <h4>Stop Loss</h4>
            <div style={{ maxWidth: 220 }}>
              <label>Stop Loss Price</label>
              <input
                type="number"
                step="0.05"
                value={sl}
                placeholder="—"
                onChange={(e) => setSl(e.target.value)}
              />
            </div>
            {avgEntry > 0 && Number(sl) > 0 && (
              <div className="hint">
                Risk {rupee((avgEntry - Number(sl)) * buyQty)} on {buyQty} shares
                {Number(sl) >= avgEntry && " — stop is at or above your average entry."}
              </div>
            )}
          </div>

          <FillGroup
            title="Sell Entries"
            rows={sells}
            onUpdate={update}
            onRemove={(key) => setRows((l) => l.filter((r) => r.key !== key))}
            onAdd={() => addRow("sell")}
            addLabel="Add sell entry"
            tone="sell"
            canRemove
          />

          {oversold && (
            <div className="banner warn">
              Sell quantity ({sellQty}) exceeds buy quantity ({buyQty}). This will be saved but
              flagged, since it can't be a real position.
            </div>
          )}
        </div>

        <div className="modal-foot">
          <span className="hint">
            {buyQty} bought{sellQty > 0 && ` · ${sellQty} sold`}
            {avgEntry > 0 && ` · avg entry ${avgEntry.toFixed(2)}`}
          </span>
          <button className="ghost" onClick={onClose}>
            Cancel
          </button>
          <button className="primary" disabled={busy || incomplete || buys.length === 0} onClick={save}>
            {busy ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

function FillGroup({
  title,
  rows,
  onUpdate,
  onRemove,
  onAdd,
  addLabel,
  tone,
  canRemove,
}: {
  title: string;
  rows: Row[];
  onUpdate: (key: string, patch: Partial<Row>) => void;
  onRemove: (key: string) => void;
  onAdd: () => void;
  addLabel: string;
  tone: "buy" | "sell";
  canRemove: boolean;
}) {
  return (
    <div className="modal-section">
      <h4>{title}</h4>
      {rows.length === 0 && <div className="hint">None yet.</div>}
      {rows.map((row) => (
        <div key={row.key} className="fill-row">
          <div>
            <label>Quantity</label>
            <input
              type="number"
              value={row.quantity}
              onChange={(e) => onUpdate(row.key, { quantity: e.target.value })}
            />
          </div>
          <div>
            <label>Price</label>
            <input
              type="number"
              step="0.05"
              value={row.price}
              onChange={(e) => onUpdate(row.key, { price: e.target.value })}
            />
          </div>
          <div>
            <label>Date</label>
            <input
              type="date"
              value={row.date}
              onChange={(e) => onUpdate(row.key, { date: e.target.value })}
            />
          </div>
          <div>
            <label>Time</label>
            <input
              type="time"
              value={row.time}
              onChange={(e) => onUpdate(row.key, { time: e.target.value })}
            />
          </div>
          <div>
            <label>Exch</label>
            <select
              value={row.exchange}
              onChange={(e) => onUpdate(row.key, { exchange: e.target.value })}
            >
              {EXCHANGES.map((x) => (
                <option key={x} value={x}>
                  {x}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label>Segment</label>
            <select
              value={row.product}
              onChange={(e) => onUpdate(row.key, { product: e.target.value })}
            >
              {PRODUCTS.map((x) => (
                <option key={x.key} value={x.key}>
                  {x.label}
                </option>
              ))}
            </select>
          </div>
          <button
            className="ghost danger"
            disabled={!canRemove}
            title={canRemove ? "Remove this fill" : "A trade needs at least one buy entry"}
            onClick={() => onRemove(row.key)}
          >
            ✕
          </button>
        </div>
      ))}
      <button className={`add-fill ${tone}`} onClick={onAdd}>
        ⊕ {addLabel}
      </button>
    </div>
  );
}
